const AUTH_RETURN_KEY = 'adlitics_auth_return';
const OAUTH_AUTHORIZE_RETURN_KEY = 'oauth_authorize_return';
const OAUTH_AUTHORIZE_PATH = '/oauth/authorize';
const OAUTH_REQUIRED_PARAMS = ['client_id', 'redirect_uri', 'state'] as const;
const MAX_OAUTH_RETURN_LENGTH = 8192;
const OAUTH_AUTHORIZE_RETURN_TTL_MS = 15 * 60 * 1000;

/** Paths we allow after login from a public auth-adjacent page. */
const ALLOWED_RETURN_PATHS = new Set(['/parceiro/aplicar', '/parceiro']);

function normalizePath(path: string): string {
  const clean = path.split('?')[0].replace(/\/$/, '') || '/';
  return clean.startsWith('/') ? clean : `/${clean}`;
}

export function setAuthReturnPath(path: string): void {
  const normalized = normalizePath(path);
  if (!ALLOWED_RETURN_PATHS.has(normalized)) return;
  try {
    sessionStorage.setItem(AUTH_RETURN_KEY, normalized);
  } catch {
    /* private mode / quota */
  }
}

/** Read and clear a safe post-login return path. */
export function consumeAuthReturnPath(): string | null {
  try {
    const raw = sessionStorage.getItem(AUTH_RETURN_KEY);
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    if (!raw) return null;
    const normalized = normalizePath(raw);
    return ALLOWED_RETURN_PATHS.has(normalized) ? normalized : null;
  } catch {
    return null;
  }
}

/**
 * Same-origin `/oauth/authorize?…` with client_id, redirect_uri and state, rebuilt as
 * `path?query`; anything else → null. UX only: oauth-server revalidates the request.
 */
export function sanitizeOAuthAuthorizeReturn(raw: string | null | undefined): string | null {
  if (!raw || raw.length > MAX_OAUTH_RETURN_LENGTH) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  let url: URL;
  try {
    url = new URL(raw, 'https://app.invalid');
  } catch {
    return null;
  }
  if (url.origin !== 'https://app.invalid') return null;
  if ((url.pathname.replace(/\/$/, '') || '/') !== OAUTH_AUTHORIZE_PATH) return null;
  if (!OAUTH_REQUIRED_PARAMS.every((key) => url.searchParams.get(key))) return null;
  return `${OAUTH_AUTHORIZE_PATH}${url.search}`;
}

/** Persist the pending consent URL before login/MFA replace the address bar. */
export function setOAuthAuthorizeReturn(pathWithQuery: string, now = Date.now()): void {
  const path = sanitizeOAuthAuthorizeReturn(pathWithQuery);
  if (!path) return;
  try {
    sessionStorage.setItem(OAUTH_AUTHORIZE_RETURN_KEY, JSON.stringify({ path, at: now }));
  } catch {
    /* private mode / quota */
  }
}

/** Read and clear the pending consent URL (only if still valid and recent). */
export function consumeOAuthAuthorizeReturn(now = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_AUTHORIZE_RETURN_KEY);
    sessionStorage.removeItem(OAUTH_AUTHORIZE_RETURN_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof saved?.path !== 'string' || typeof saved.at !== 'number') return null;
    if (now - saved.at > OAUTH_AUTHORIZE_RETURN_TTL_MS || saved.at > now) return null;
    return sanitizeOAuthAuthorizeReturn(saved.path);
  } catch {
    return null;
  }
}
